import {orderTypes} from "@/domain/cart/constants";

const state = {
    cartDigitalItems: [],
    cartDigitalItemsTotals: null,
    totalCartItems: null,
    payments: null,
    campaign: localStorage.getItem('campaign') || '',
}

const getters = {
    cartDigitalItems: state => state.cartDigitalItems,
    cartDigitalItemsTotals: state => state.cartDigitalItemsTotals,
    totalCartItems: state => state.totalCartItems,
    paymentMethods: state => state.payments && state.payments.payment_methods,
    paymentSettings: state => state.payments && state.payments.payment_settings,
    mediaIsInCart: state => (digitalItemId, mediaId) => {
        const digitalItem = state.cartDigitalItems.find(digitalItem => digitalItem.id === digitalItemId)
        return digitalItem && digitalItem.media_ids.includes(mediaId)
    },
    getDigitalItemByPicaCode: state => picacode => {
        return state.cartDigitalItems.find(digitalItem => digitalItem.picacode === picacode)
    },
    getDigitalItemById: state => digitalItemId => {
        return state.cartDigitalItems.find(digitalItem => digitalItem.id === digitalItemId)
    },
    getDigitalItemByGalleryId: state => galleryId => {
        return state.cartDigitalItems.find(digitalItem => digitalItem.gallery_id === galleryId)
    },
    mediaInCartCount: (state) => (digitalItemId) => {
        const item = state.cartDigitalItems.find(item => item.id === digitalItemId)
        return item ? item.media_ids.length : 0
    },
    campaignCode: state => state.campaign && JSON.parse(state.campaign).campaign_code,
    campaignCodeExpDate: state => state.campaign && JSON.parse(state.campaign).campaign_expires_at,
    campaignIsValid: (state, getters) => {
        const campaignCode = getters.campaignCode
        if (!campaignCode) return false

        const today = new Date()
        const campaignExpireDate = new Date(getters.campaignCodeExpDate);
        return campaignExpireDate >= today
    },
}

const mutations = {
    setCartDigitalItems(state, cartDigitalItems) {
        state.cartDigitalItems = cartDigitalItems
    },
    setTotalCartItems(state, totalCartItems) {
        state.totalCartItems = totalCartItems
    },

    removeMediasFromDigitalItem(state, {digitalItem, params}) {
        digitalItem.media_ids = digitalItem.media_ids.filter(mediaId => !params.media_ids.includes(mediaId))
        if (digitalItem.medias)
            digitalItem.medias = digitalItem.medias.filter(media => !params.media_ids.includes(media.id))
        digitalItem.order_type = orderTypes.PHOTOLIST
        // force update
        state.cartDigitalItems = [...state.cartDigitalItems]
    },
    removeDigitalItem(state, digitalItemId) {
        state.cartDigitalItems = state.cartDigitalItems.filter(digitalItem => digitalItem.id !== digitalItemId)
    },
    addMediasToDigitalItem(state, {digitalItemId, params}) {
        const digitalItem = state.cartDigitalItems.find(item => item.id === digitalItemId);
        if (digitalItem) {
            digitalItem.media_ids.push(...params.media_ids);
        }
    },
    setPayments(state, payments) {
        state.payments = payments
    },
    setCartDigitalItemsMedias(state, {digitalItemId, medias}) {
        state.cartDigitalItems = state.cartDigitalItems.map(digitalItem => {
            if (digitalItem.id === digitalItemId) {
                digitalItem.medias = medias
            }
            return digitalItem
        })
    },
    setDigitalItemsTotals(state, cartDigitalItemsTotals) {
        state.cartDigitalItemsTotals = cartDigitalItemsTotals
    },
    deleteCart(state) {
        state.cartDigitalItems = []
        state.cartDigitalItemsTotals = null
        state.totalCartItems = null
        state.payments = null
    },
    setCampaign(state, campaign) {
        state.campaign = campaign
    },
    deleteCampaign() {
        state.campaign = ''
    }
}

export const actions = (cartService) => ({
        async fetchDigitalItems(context, includeMedias = false) {
            const response = await cartService.getDigitalItems()
            context.commit('setCartDigitalItems', response.results)

            // get medias for each digital item
            if (includeMedias) {
                for (const digitalItem of response.results) {
                    await context.dispatch('fetchDigitalItemMedias', {
                        digitalItemId: digitalItem.id,
                        params: {page: 1, page_size: 99999}
                    })
                }
            }
            return response
        },
        async removeMediasFromDigitalItem(context, {digitalItemId, params}) {
            const response = cartService.removeMediasFromDigitalItem(digitalItemId, params)
            const digitalItem = context.getters.getDigitalItemById(digitalItemId)
            context.commit('removeMediasFromDigitalItem', {digitalItem, params})
            return response
        },
        async removeDigitalItem(context, digitalItemId) {
            const response = await cartService.removeDigitalItem(digitalItemId)
            context.commit('removeDigitalItem', digitalItemId)
            return response
        },
        async fetchTotalCartItems(context) {
            try {
                const response = await cartService.getTotals()
                context.commit('setTotalCartItems', response)
                return response
            } catch (e) {
                context.commit('setTotalCartItems', null)
                return false
            }
        },
        async createDigitalItem(context, params) {
            await cartService.createDigitalItem(params)
            await context.dispatch('fetchDigitalItems')
            await context.dispatch('emitFbPixelAddToCartEvent')
        },
        async setDigitalItemType(context, {digitalItemId, params}) {
            await cartService.setDigitalItemType(digitalItemId, params)
            await context.dispatch('fetchDigitalItems')
            await context.dispatch('emitFbPixelAddToCartEvent')
        },
        async addMediasToDigitalItem(context, {digitalItemId, params}) {
            const response = await cartService.addMediasToDigitalItem(digitalItemId, params)
            context.commit('addMediasToDigitalItem', {digitalItemId, params})
            await context.dispatch('emitFbPixelAddToCartEvent')
            return response
        },
        async addAllMediasToDigitalItem(context, params) {
            await context.dispatch('emitFbPixelAddToCartEvent')
            return await cartService.addAllMediasToDigitalItem(params)
        },
        async fetchPayments(context) {
            try {
                const response = await cartService.getPayments()
                context.commit('setPayments', response)
                return response
            } catch (e) {
                context.commit('setPayments', null)
                return false
            }
        },
        async fetchDigitalItemMedias(context, {digitalItemId, params}) {
            const response = await cartService.getDigitalItemMedias(digitalItemId, params)
            context.commit('setCartDigitalItemsMedias', {digitalItemId, medias: response.results})
            return response
        },
        async fetchDigitalItemsTotals(context) {
            try {
                const response = await cartService.getDigitalItemsTotals()
                context.commit('setDigitalItemsTotals', response)
                return response
            } catch (e) {
                context.commit('setDigitalItemsTotals', null)
                return false
            }
        },
        async createOrderIntent(context, params) {
            return await cartService.createOrderIntent(params)
        },

        async createOrder(context, params = {}) {
            params.platform = 'site';

            const campaignCode = context.getters.campaignCode;
            if (campaignCode && context.getters.campaignIsValid) {
                params.campaign_code = campaignCode;
            } else {
                localStorage.removeItem('campaign')
                context.commit('deleteCampaign')
            }

            return await cartService.createOrder(params);
        },
        async fetchOrder(context, orderId) {
            try {
                return await cartService.getOrder(orderId)
            }catch (e) {
                return false
            }
        },
        async deleteCart(context) {
            try {
                const response = await cartService.deleteCart()
                context.commit('deleteCart')
                return response
            } catch (e) {
                return false
            }
        },
        emitFbPixelAddToCartEvent() {
            // eslint-disable-next-line no-undef
            if (typeof fbq === 'function') fbq('track', 'AddToCart')
        },
        emitFbPixelPurchaseEvent(context, {currency, amount}) {
            if (typeof fbq === 'function') {
                // eslint-disable-next-line
                fbq('track', 'Purchase', {
                    currency: currency,
                    value: amount,
                    num_items: context.state.cartDigitalItems.length
                })
            }

        },
    }
)


export default cartService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(cartService),
})